import AdvancedModal from '@portal/components/AdvancedModal/AdvancedModal';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import useReduxState from '@portal/hooks/useReduxState';
import React, { ReactNode } from 'react';
import './GenericModal.less';

type ModalAction = {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'outlined';
  disabled?: boolean;
};

type MoreDescriptionType = {
  text: string;
  icon?: React.ReactElement;
};

type GenericModalProps = {
  visible: boolean;
  title?: string;
  description?: string;
  actions?: ModalAction[];
  onClose: () => void;
  children?: ReactNode;
  size?: 'small' | 'medium' | 'large';
  moreDescription?: MoreDescriptionType[];
  secondTitle?: string;
};

const GenericModal = ({
  visible,
  title,
  description,
  actions = [],
  onClose,
  children,
  size = 'medium',
  moreDescription = [],
  secondTitle,
}: GenericModalProps) => {
  const {
    theme: { currentTheme },
  } = useReduxState();

  const hasMoreDescription = moreDescription.length > 0;
  const hasActions = actions.length > 0;

  const getActionStyles = (variant: ModalAction['variant'] = 'primary') => {
    switch (variant) {
      case 'outlined':
        return {
          background: 'transparent',
          border: `1px solid ${currentTheme.borderColor}`,
          color: currentTheme.black,
        };
      case 'secondary':
        return {
          background: currentTheme.background,
          border: `1px solid ${currentTheme.borderColor}`,
          color: currentTheme.black,
        };
      case 'primary':
      default:
        return {
          background: currentTheme.background,
          border: `1px solid ${currentTheme.borderColor}`,
          color: currentTheme.white,
        };
    }
  };

  return (
    <AdvancedModal
      visible={visible}
      size={size}
      onClose={onClose}
      title={title ?? ''}
      autoHeight
    >
      {description ? (
        <ThemedText variant="body" className="generic-modal__description">
          {description}
        </ThemedText>
      ) : null}
      {hasMoreDescription
        ? moreDescription.map(({ text, icon }) => (
            <div key={text} className="generic-modal__more_description__block">
              {icon}
              <ThemedText
                variant="body"
                className="generic-modal__more_description"
              >
                {text.split(', ')}
              </ThemedText>
            </div>
          ))
        : null}
      {secondTitle ? (
        <ThemedText variant="body" className="generic-modal__secondTitle">
          {secondTitle}
        </ThemedText>
      ) : null}
      {children}
      {hasActions ? (
        <div className="generic-modal__actions">
          {actions.map((action) => {
            const styles = getActionStyles(action.variant);

            return (
              <button
                key={action.label}
                onClick={action.onClick}
                disabled={action.disabled}
                className="generic-modal__footer"
                style={styles}
              >
                <span style={{ color: styles.color }}>{action.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </AdvancedModal>
  );
};

export default GenericModal;
